import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Modal, TouchableOpacity, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import Toast from 'react-native-toast-message';
import { deacService, VacancyAbsence } from '../../../services/central-api/deac';
import MyDataTable from '../../../components/MyDataTable';
import PresenceSituationDropdown from '../../../components/deac/PresenceSituationDropdown';
import AbsenceTypeDropdown from '../../../components/deac/AbsenceTypeDropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import { getPresenceStatus } from '../Vacancies/VacanciesCommons';
import PresenceAssignmentModal from './PresenceAssignmentModal';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    selectedApplications: Presence[];
    setSelectedApplications: Dispatch<SetStateAction<Presence[]>>;
    onConfirm: () => void;
    onClose: () => void;
}

export default function PresenceRegistrationModal({ selectedApplications, setSelectedApplications, isModalVisible, setModalVisible, onConfirm, onClose }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [step, setStep] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [presenceSituation, setPresenceSituation] = useState<boolean | null>(null);
    const [absenceType, setAbsenceType] = useState<VacancyAbsence | null>(null);

    useEffect(() => {
        if (isModalVisible && selectedApplications.length === 0) {
            closeModal();
        }
    }, [selectedApplications]);

    async function formSubmit() {
        try {
            setIsLoading(true);

            await deacService.presenceRegistration({
                present: presenceSituation ?? false,
                absentJustification: absenceType ? absenceType : undefined,
                applications: Object.fromEntries(
                    selectedApplications.map(sa => [sa.id, sa.assignmentId ?? null])
                )
            });

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            closeModal();
            onConfirm();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }

    function isFormValid(): boolean {
        if (!presenceSituation && absenceType === null) {
            return false;
        }

        if (presenceSituation && selectedApplications.some(sa => !sa.assignmentId)) {
            return false;
        }

        return true;
    }

    const closeModal = () => {
        setModalVisible(false);
        setStep(0);
        setPresenceSituation(null);
        setAbsenceType(null);
        onClose();
    };

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => closeModal()}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    <View style={{ flexDirection: 'column', rowGap: 8 }}>
                        <MyAppText style={styles.header}>{translate('agentPresenceRegistration')}</MyAppText>
                        <MyAppText style={styles.subheader}>{translate('agentsWillHavePresenceConfirmed')}</MyAppText>
                        <MyAppText style={styles.stepHeader}>{translate('stepXofY', { x: step + 1, y: 2 })}</MyAppText>
                    </View>
                    {step === 0
                        ? <View style={{ flex: 1, gap: 10 }}>
                            <View style={styles.cardList}>
                                <MyDataTable
                                    columns={[
                                        {
                                            name: translate('status'),
                                            cell: row => getPresenceStatus(row),
                                            grow: 0,
                                        },
                                        {
                                            name: translate('agentName'),
                                            cell: row => row.user.warName && row.user.warName != '' ? row.user.warName : row.user.name,
                                            grow: 0,
                                        },
                                        {
                                            name: 'RF',
                                            cell: row => row.user.registry,
                                            grow: 0,
                                        },
                                        {
                                            name: translate('assignment'),
                                            cell: row => {
                                                if (row.assignment) {
                                                    return row.assignment.name;
                                                }
                                                return '';
                                            },
                                            grow: 1,
                                        },
                                        {
                                            name: translate('role'),
                                            cell: row => row.user.role.name,
                                            grow: 0,
                                        },
                                        {
                                            name: translate('vacancyId'),
                                            cell: row => row.vacancy.id,
                                            grow: 0,
                                            wrap: true
                                        },
                                        {
                                            name: translate('activityTitle'),
                                            cell: row => row.vacancy.title,
                                            grow: 1,
                                            wrap: true
                                        },
                                        {
                                            name: translate('workplace'),
                                            cell: row => row.vacancy.workplace.name,
                                            grow: 1,
                                            wrap: true
                                        },
                                        {
                                            name: translate('agentActingBody'),
                                            cell: row => row.user.actingBodyUnit.actingBody.name,
                                            grow: 1,
                                        },
                                        {
                                            name: translate('agentUnit'),
                                            cell: row => row.user.actingBodyUnit.name,
                                            grow: 1,
                                        },
                                        {
                                            grow: 0,
                                            width: '50px',
                                            cell: row => <TouchableOpacity onPress={() => {
                                                setSelectedApplications([...selectedApplications.filter(application => application.id != row.id)]);
                                            }}>
                                                <FontAwesomeIcon icon={faClose} color={theme.colors.errors['500']} fontSize={14} />
                                            </TouchableOpacity>
                                        }
                                    ]}
                                    data={selectedApplications}
                                />
                            </View>
                            <MyAppText style={styles.selectedAgents}>{translate('selectedAgents')}: {selectedApplications.length}</MyAppText>
                        </View>
                        : <View style={{ flexDirection: 'column', gap: 8, flex: 1 }}>
                            <PresenceSituationDropdown
                                setValue={setPresenceSituation}
                                value={presenceSituation}
                                zIndex={2}
                                labelStyle={styles.fieldsLabelStyle}
                                viewStyle={{ gap: 8 }}
                            />
                            {presenceSituation &&
                                <PresenceAssignmentModal
                                    selectedApplications={selectedApplications}
                                    setSelectedApplications={setSelectedApplications}
                                    presenceSituation={presenceSituation}
                                />
                            }
                            {presenceSituation === false
                                ? <AbsenceTypeDropdown
                                    setValue={setAbsenceType}
                                    value={absenceType}
                                    zIndex={1}
                                    labelStyle={styles.fieldsLabelStyle}
                                    viewStyle={{ gap: 8 }}
                                />
                                : null}
                        </View>
                    }
                    <FormActions
                        buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9, }}
                        onSubmit={() => step === 0 ? setStep(1) : formSubmit()}
                        onClose={() => step === 1 ? setStep(0) : closeModal()}
                        disabled={(step === 1 && !isFormValid()) || isLoading}
                        isLoading={isLoading}
                        cancelText={step === 0 ? 'cancel' : 'goBack'}
                        confirmText={step === 0 ? 'forward' : 'conclude'}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
    },
    formContainer: (isMobile: boolean) => ({
        width: isMobile ? '100%' : 'auto',
        maxWidth: isMobile ? '100%' : '80%',
        maxHeight: '75%',
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 32
    }),
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 14,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    stepHeader: {
        fontSize: 16,
        fontWeight: '600',
        flexWrap: 'wrap',
        color: theme.colors.primary['500'],
    },
    cardList: {
        borderWidth: 1,
        borderRadius: 4,
        borderColor: theme.colors.neutral['200'],
        backgroundColor: theme.colors.dataTable.background,
        flex: 1,
    },
    selectedAgents: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
        alignSelf: 'flex-end'
    },
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    }
}));
